import React, { useState, useEffect, Fragment } from 'react'
import { setupAPI } from '../../utils/apiHelpers'

const EnvironmentSection = ({ token }) => {
    const [apiStatus, setApiStatus] = useState('checking');
    const [dbStatus, setDbStatus] = useState('checking');
    const [setupStatus, setSetupStatus] = useState(null);
    const [isInitializing, setIsInitializing] = useState(false);
    const [initProgress, setInitProgress] = useState([]);
    const [showModal, setShowModal] = useState(false);
    const [tableCounts, setTableCounts] = useState({});
    const [tableSizes, setTableSizes] = useState({});
    const [tableColumns, setTableColumns] = useState({});
    const [loadingTables, setLoadingTables] = useState({});
    const [selectedTableModal, setSelectedTableModal] = useState(null);
    const [databaseSize, setDatabaseSize] = useState(null);
    const [databaseSizeLoading, setDatabaseSizeLoading] = useState(false);

    const formatBytes = (bytes) => {
        if (bytes === 0) return '0 B';
        const k = 1024;
        const sizes = ['B', 'KB', 'MB', 'GB', 'TB'];
        const i = Math.floor(Math.log(bytes) / Math.log(k));
        return parseFloat((bytes / Math.pow(k, i)).toFixed(2)) + ' ' + sizes[i];
    };

    const GB = 1024 ** 3;

    // Table cards default to green; cross 1GB and they warn (amber), cross 5GB
    // and they flag as heavy (red) - purely a visual cue, no size is unknown.
    const getTableSizeBorderClass = (bytes) => {
        if (bytes === undefined || bytes === null) return 'border-l-green-500 dark:border-l-green-400';
        if (bytes >= 5 * GB) return 'border-l-red-500 dark:border-l-red-400';
        if (bytes >= 1 * GB) return 'border-l-amber-500 dark:border-l-amber-400';
        return 'border-l-green-500 dark:border-l-green-400';
    };

    useEffect(() => {
        let isFirstCall = true;

        const checkStatus = async () => {
            try {
                const silent = !isFirstCall;
                const health = await setupAPI.healthCheck(silent);
                setApiStatus('healthy');
                const status = await setupAPI.getStatus(token, silent);
                setDbStatus(status.database_connected ? 'connected' : 'disconnected');
                setSetupStatus(status);

                // Fetch table record counts
                const counts = await setupAPI.getTableCounts(silent);
                setTableCounts(counts);

                // Fetch per-table storage sizes
                const sizes = await setupAPI.getTableSizes(silent);
                setTableSizes(sizes);

                // Fetch database size
                setDatabaseSizeLoading(true);
                const sizeData = await setupAPI.getDatabaseSize(silent);
                setDatabaseSize(sizeData);
                setDatabaseSizeLoading(false);

                isFirstCall = false;
            } catch (error) {
                setApiStatus('unhealthy');
                isFirstCall = false;
            }
        };
        checkStatus();
        const interval = setInterval(checkStatus, 30000);
        return () => clearInterval(interval);
    }, [token]);

    const openTableModal = async (tableName) => {
        setSelectedTableModal(tableName);

        // Load columns if not already loaded
        if (!tableColumns[tableName]) {
            setLoadingTables(prev => ({
                ...prev,
                [tableName]: true
            }));

            try {
                const columns = await setupAPI.getTableColumns(tableName);
                setTableColumns(prev => ({
                    ...prev,
                    [tableName]: columns
                }));
            } catch (error) {
                setTableColumns(prev => ({
                    ...prev,
                    [tableName]: []
                }));
            } finally {
                setLoadingTables(prev => ({
                    ...prev,
                    [tableName]: false
                }));
            }
        }
    };

    const handleInitialize = async () => {
        setIsInitializing(true);
        setShowModal(true);
        setInitProgress([]);
        try {
            const result = await setupAPI.initDatabase(token);
            if (result.progress_steps) {
                result.progress_steps.forEach((step, index) => {
                    setTimeout(() => {
                        setInitProgress(prev => [...prev, step]);
                    }, (index + 1) * 500);
                });
            }
            setTimeout(async () => {
                const status = await setupAPI.getStatus(token);
                setSetupStatus(status);
                setIsInitializing(false);
            }, result.progress_steps.length * 500 + 1000);
        } catch (error) {
            setInitProgress(prev => [...prev, {
                table: 'erro',
                status: 'error',
                message: `Erro ao inicializar: ${error.message}`
            }]);
            setIsInitializing(false);
        }
    };

    const statusConfig = {
        healthy:      { label: 'Saudável',      color: 'bg-green-100 dark:bg-green-900/30 text-green-700 dark:text-green-400', dot: 'bg-green-500' },
        unhealthy:    { label: 'Indisponível',   color: 'bg-red-100 dark:bg-red-900/30 text-red-700 dark:text-red-400',       dot: 'bg-red-500' },
        connected:    { label: 'Conectado',      color: 'bg-green-100 dark:bg-green-900/30 text-green-700 dark:text-green-400', dot: 'bg-green-500' },
        disconnected: { label: 'Desconectado',   color: 'bg-red-100 dark:bg-red-900/30 text-red-700 dark:text-red-400',       dot: 'bg-red-500' },
        checking:     { label: 'Verificando…',   color: 'bg-yellow-100 dark:bg-yellow-900/30 text-yellow-700 dark:text-yellow-400', dot: 'bg-yellow-400' },
    };

    const StatusBadge = ({ status }) => {
        const cfg = statusConfig[status] || statusConfig.checking;
        return (
            <span className={`inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full text-xs font-medium ${cfg.color}`}>
                <span className={`w-1.5 h-1.5 rounded-full ${cfg.dot}`} />
                {cfg.label}
            </span>
        );
    };

    const iconClass = (color) =>
        `material-icons text-2xl ${color}`;

    const progress = setupStatus?.progress ?? 0;
    const allCreated = setupStatus?.all_tables_created ?? false;
    const sortedTables = setupStatus
        ? Object.entries(setupStatus.tables).sort(([a], [b]) => a.localeCompare(b))
        : [];

    return (
        <div>
            {/* Header */}
            <div className="border-b border-gray-200 dark:border-gray-700 p-6">
                <h2 className="text-xl font-semibold">Ambiente</h2>
                <p className="text-gray-500 dark:text-gray-400 text-sm mt-1">
                    Status da API, banco de dados e setup do ambiente
                </p>
            </div>

            <div className="p-6 space-y-6">
                {/* ── Status cards row ── */}
                <div className="grid grid-cols-1 lg:grid-cols-4 gap-4">

                    {/* API Service */}
                    <div className="flex items-start gap-4 bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-700 rounded-xl p-4 shadow-sm">
                        <div className="flex-shrink-0 w-10 h-10 flex items-center justify-center rounded-lg bg-blue-50 dark:bg-blue-900/30">
                            <span className={iconClass('text-blue-500 dark:text-blue-400')}>cloud</span>
                        </div>
                        <div className="min-w-0 flex-1">
                            <p className="text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wide mb-1">API Service</p>
                            <StatusBadge status={apiStatus} />
                            <p className="text-xs text-gray-400 dark:text-gray-500 mt-2 truncate">
                                {import.meta.env.VITE_API_URL || 'http://localhost:8000'}
                            </p>
                        </div>
                    </div>

                    {/* Banco de Dados */}
                    <div className="lg:col-span-3 bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-700 rounded-xl shadow-sm overflow-hidden">
                        <div className="border-b border-gray-200 dark:border-gray-700 px-5 py-4">
                            <h3 className="text-sm font-semibold text-gray-900 dark:text-gray-100">Banco de Dados</h3>
                        </div>
                        <div className="grid grid-cols-3 gap-4 p-4">
                            {/* Provedor */}
                            <div className="flex items-start gap-4">
                                <div className="flex-shrink-0 w-10 h-10 flex items-center justify-center rounded-lg bg-purple-50 dark:bg-purple-900/30">
                                    <span className={iconClass('text-purple-500 dark:text-purple-400')}>storage</span>
                                </div>
                                <div className="min-w-0 flex-1">
                                    <p className="text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wide mb-1">Provedor</p>
                                    {setupStatus ? (
                                        <>
                                            <span className="inline-flex items-center px-2.5 py-1 rounded-full text-xs font-medium bg-purple-100 dark:bg-purple-900/30 text-purple-700 dark:text-purple-400">
                                                {setupStatus.provider.display}
                                            </span>
                                            {setupStatus.provider.provider === 'sqlite' && (
                                                <p className="text-xs text-gray-400 dark:text-gray-500 mt-2 truncate">
                                                    {setupStatus.provider.path || 'hub_platform.db'}
                                                </p>
                                            )}
                                        </>
                                    ) : (
                                        <StatusBadge status="checking" />
                                    )}
                                </div>
                            </div>

                            {/* Conexão DB */}
                            <div className="flex items-start gap-4">
                                <div className="flex-shrink-0 w-10 h-10 flex items-center justify-center rounded-lg bg-teal-50 dark:bg-teal-900/30">
                                    <span className={iconClass('text-teal-500 dark:text-teal-400')}>cable</span>
                                </div>
                                <div className="min-w-0 flex-1">
                                    <p className="text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wide mb-1">Conexão</p>
                                    <StatusBadge status={dbStatus} />
                                </div>
                            </div>

                            {/* Tamanho do Banco de Dados */}
                            <div className="flex items-start gap-4">
                                <div className="flex-shrink-0 w-10 h-10 flex items-center justify-center rounded-lg bg-orange-50 dark:bg-orange-900/30">
                                    <span className={iconClass('text-orange-500 dark:text-orange-400')}>data_usage</span>
                                </div>
                                <div className="min-w-0 flex-1">
                                    <p className="text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wide mb-1">Tamanho</p>
                                    {databaseSizeLoading ? (
                                        <div className="flex items-center gap-1">
                                            <span className="material-icons text-sm text-gray-400 dark:text-gray-500 animate-spin" style={{ fontSize: 14 }}>
                                                settings
                                            </span>
                                            <span className="text-xs text-gray-500 dark:text-gray-400">Carregando…</span>
                                        </div>
                                    ) : databaseSize ? (
                                        <span className="inline-flex items-center px-2.5 py-1 rounded-full text-xs font-medium bg-orange-100 dark:bg-orange-900/30 text-orange-700 dark:text-orange-400">
                                            {formatBytes(databaseSize.size_bytes)}
                                        </span>
                                    ) : (
                                        <span className="text-xs text-gray-500 dark:text-gray-400">Indisponível</span>
                                    )}
                                </div>
                            </div>
                        </div>
                    </div>
                </div>

                {/* ── Tabelas ── */}
                {setupStatus && (
                    <div className="border border-gray-200 dark:border-gray-700 rounded-xl overflow-hidden">
                        <div className="flex items-center gap-3 px-5 py-4 border-b border-gray-200 dark:border-gray-700">
                            <span className="material-icons text-gray-400 dark:text-gray-500">table_chart</span>
                            <div>
                                <h3 className="text-sm font-semibold text-gray-900 dark:text-gray-100">
                                    Tabelas do Banco de Dados
                                </h3>
                                <p className="text-xs text-gray-400 dark:text-gray-500 mt-0.5">
                                    {sortedTables.filter(([, v]) => v).length} de {sortedTables.length} criadas
                                </p>
                            </div>
                        </div>

                        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4 p-4 bg-gray-50 dark:bg-gray-800/30 rounded-lg">
                            {sortedTables.map(([table, exists]) => {
                                const sizeBytes = tableSizes[table];
                                return (
                                    <button
                                        key={table}
                                        onClick={() => exists && openTableModal(table)}
                                        disabled={!exists}
                                        className={`border border-gray-200 dark:border-gray-700 rounded-lg overflow-hidden ${
                                            exists ? `border-l-4 ${getTableSizeBorderClass(sizeBytes)} cursor-pointer hover:shadow-md transition` : 'opacity-50 cursor-not-allowed'
                                        } text-left`}
                                    >
                                        <div className="bg-white dark:bg-gray-800 flex items-start justify-between gap-2 px-4 py-3">
                                            <div className="flex items-start gap-2 min-w-0">
                                                <span
                                                    className={`material-icons text-base flex-shrink-0 mt-0.5 ${
                                                        exists
                                                            ? 'text-blue-500 dark:text-blue-400'
                                                            : 'text-gray-300 dark:text-gray-600'
                                                    }`}
                                                >
                                                    table_chart
                                                </span>
                                                <div className="min-w-0">
                                                    <span className="text-sm text-gray-700 dark:text-gray-300 font-mono block truncate">
                                                        {table}
                                                    </span>
                                                    {exists && tableCounts[table] !== undefined && (
                                                        <span className="inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium bg-green-100 dark:bg-green-900/30 text-green-700 dark:text-green-300 mt-1">
                                                            {tableCounts[table]} registro{tableCounts[table] !== 1 ? 's' : ''}
                                                        </span>
                                                    )}
                                                </div>
                                            </div>
                                            {exists && sizeBytes !== undefined && (
                                                <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-[11px] font-medium bg-orange-100 dark:bg-orange-900/30 text-orange-700 dark:text-orange-400 flex-shrink-0">
                                                    <span className="material-icons" style={{ fontSize: 12 }}>data_usage</span>
                                                    {formatBytes(sizeBytes)}
                                                </span>
                                            )}
                                        </div>
                                    </button>
                                );
                            })}
                        </div>
                    </div>
                )}
            </div>

            {/* ── Modal de tabela ── */}
            {selectedTableModal && (
                <Fragment>
                    <div className="fixed inset-0 bg-black/50 z-30" onClick={() => setSelectedTableModal(null)} />
                    <div className="fixed right-0 top-0 h-screen w-full max-w-md bg-white dark:bg-gray-800 shadow-lg z-40 flex flex-col">
                        <div className="p-6 border-b border-gray-200 dark:border-gray-700 flex-shrink-0">
                            <div className="flex items-start justify-between gap-4">
                                <div className="flex items-start gap-3">
                                    <span className="material-icons text-blue-500 dark:text-blue-400 mt-0.5">table_chart</span>
                                    <div>
                                        <h2 className="text-xl font-semibold text-gray-900 dark:text-white">{selectedTableModal}</h2>
                                        {tableCounts[selectedTableModal] !== undefined && (
                                            <span className="inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium bg-green-100 dark:bg-green-900/30 text-green-700 dark:text-green-300 mt-1">
                                                {tableCounts[selectedTableModal]} registro{tableCounts[selectedTableModal] !== 1 ? 's' : ''}
                                            </span>
                                        )}
                                    </div>
                                </div>
                                <button
                                    onClick={() => setSelectedTableModal(null)}
                                    className="p-1 hover:bg-gray-100 dark:hover:bg-gray-700 rounded-lg transition flex-shrink-0"
                                >
                                    <span className="material-icons sm">close</span>
                                </button>
                            </div>
                        </div>

                        <div className="p-6 overflow-y-auto flex-1">

                            {/* Colunas */}
                            <div>
                                <div className="flex items-center gap-2 mb-3">
                                    <h3 className="text-sm font-semibold text-gray-700 dark:text-gray-300">Colunas</h3>
                                    {tableColumns[selectedTableModal] && tableColumns[selectedTableModal].length > 0 && (
                                        <span className="inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium bg-gray-100 dark:bg-gray-700 text-gray-700 dark:text-gray-300">
                                            {tableColumns[selectedTableModal].length}
                                        </span>
                                    )}
                                </div>
                                <div className="border border-gray-200 dark:border-gray-700 rounded-lg overflow-hidden divide-y divide-gray-200 dark:divide-gray-700">
                                    {loadingTables[selectedTableModal] ? (
                                        <div className="px-4 py-8 text-center">
                                            <span className="material-icons text-3xl text-gray-400 dark:text-gray-500 animate-spin inline-block">
                                                settings
                                            </span>
                                            <p className="text-sm text-gray-500 dark:text-gray-400 mt-2">Carregando colunas…</p>
                                        </div>
                                    ) : tableColumns[selectedTableModal] && tableColumns[selectedTableModal].length > 0 ? (
                                        tableColumns[selectedTableModal].map(col => (
                                            <div key={col.name} className="flex items-start gap-2 px-4 py-3 text-sm">
                                                <span className={`material-icons text-base flex-shrink-0 mt-0.5 ${
                                                    col.primary_key ? 'text-amber-500 dark:text-amber-400' : 'text-gray-400 dark:text-gray-500'
                                                }`}>
                                                    {col.primary_key ? 'vpn_key' : 'circle'}
                                                </span>
                                                <div className="flex-1 min-w-0">
                                                    <div className="flex items-center gap-2">
                                                        <span className="font-mono text-gray-700 dark:text-gray-300">{col.name}</span>
                                                        {col.primary_key && (
                                                            <span className="inline-flex items-center px-1.5 py-0.5 rounded text-xs font-medium bg-amber-100 dark:bg-amber-900/30 text-amber-700 dark:text-amber-300">
                                                                PK
                                                            </span>
                                                        )}
                                                        {!col.nullable && (
                                                            <span className="inline-flex items-center px-1.5 py-0.5 rounded text-xs font-medium bg-red-100 dark:bg-red-900/30 text-red-700 dark:text-red-300">
                                                                NOT NULL
                                                            </span>
                                                        )}
                                                    </div>
                                                    <p className="text-xs text-gray-500 dark:text-gray-400 mt-0.5 font-mono">
                                                        {col.type}
                                                    </p>
                                                </div>
                                            </div>
                                        ))
                                    ) : (
                                        <div className="px-4 py-4 text-center text-gray-500 dark:text-gray-400">
                                            <p className="text-sm">Nenhuma coluna encontrada</p>
                                        </div>
                                    )}
                                </div>
                            </div>
                        </div>
                    </div>
                </Fragment>
            )}

            {/* ── Modal de progresso ── */}
            {showModal && (
                <Fragment>
                    <div className="fixed inset-0 bg-black/50 z-30" />
                    <div className="fixed inset-0 flex items-center justify-center z-40 p-4">
                        <div className="bg-white dark:bg-gray-800 rounded-xl shadow-xl max-w-md w-full max-h-96 overflow-y-auto">
                            <div className="sticky top-0 bg-white dark:bg-gray-800 border-b border-gray-200 dark:border-gray-700 px-5 py-4 flex items-center justify-between">
                                <div className="flex items-center gap-2">
                                    <span className="material-icons text-amber-500" style={{ fontSize: 20 }}>settings</span>
                                    <h3 className="font-semibold text-gray-900 dark:text-gray-100 text-sm">
                                        Progresso da Inicialização
                                    </h3>
                                </div>
                                {!isInitializing && (
                                    <button
                                        onClick={() => setShowModal(false)}
                                        className="p-1 hover:bg-gray-100 dark:hover:bg-gray-700 rounded-lg transition"
                                    >
                                        <span className="material-icons text-gray-500" style={{ fontSize: 18 }}>close</span>
                                    </button>
                                )}
                            </div>
                            <div className="p-4 space-y-2">
                                {initProgress.length === 0 ? (
                                    <div className="text-center py-8">
                                        <span className="material-icons text-gray-300 dark:text-gray-600 text-4xl">hourglass_empty</span>
                                        <p className="text-sm text-gray-500 dark:text-gray-400 mt-2">Aguarde…</p>
                                    </div>
                                ) : (
                                    initProgress.map((step, index) => {
                                        const cfg = step.status === 'created'
                                            ? { bg: 'bg-green-50 dark:bg-green-900/20 border-green-200 dark:border-green-800', icon: 'check_circle', iconColor: 'text-green-500' }
                                            : step.status === 'skipped'
                                            ? { bg: 'bg-yellow-50 dark:bg-yellow-900/20 border-yellow-200 dark:border-yellow-800', icon: 'skip_next', iconColor: 'text-yellow-500' }
                                            : { bg: 'bg-red-50 dark:bg-red-900/20 border-red-200 dark:border-red-800', icon: 'error', iconColor: 'text-red-500' };
                                        return (
                                            <div key={index} className={`flex items-start gap-3 p-3 rounded-lg border ${cfg.bg}`}>
                                                <span className={`material-icons text-base flex-shrink-0 mt-0.5 ${cfg.iconColor}`}>{cfg.icon}</span>
                                                <div className="min-w-0">
                                                    <p className="text-sm font-medium text-gray-800 dark:text-gray-200 font-mono">{step.table}</p>
                                                    <p className="text-xs text-gray-500 dark:text-gray-400">{step.message}</p>
                                                </div>
                                            </div>
                                        );
                                    })
                                )}
                            </div>
                        </div>
                    </div>
                </Fragment>
            )}
        </div>
    );
};

export default EnvironmentSection
